import type {
  AccountSummary,
  AuthSessionResponse,
  BusinessProfile,
  UpsertBusinessProfileRequest,
  CoachTaskRecord,
  CreatePaymentResponse,
  Order,
  PaymentProviderName,
  Plan,
  PointRulesResponse,
  PointLedgerEntry,
  Product,
  GrowthPlatform,
  GrowthRoute,
  CommercialReadiness,
  ReconciliationSummary,
  HumanSupportDraft,
  HumanSupportContext,
  HumanSupportTicket,
  KnowledgeItem,
  KnowledgeReviewItem,
  ProductionVerificationKey,
} from "@cross-border-agent/contracts";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:3000").replace(/\/$/, "");
export const SESSION_KEY = "crossBorderAgentSession";
export const SYNC_META_KEY = "xiaomanWebSessionSync";
export type WebSyncMeta = { id: string; kind: "login" | "logout" | "sync" | "expired"; seenExtension?: string; at?: number };
export function loadSyncMeta(): WebSyncMeta | null {
  try { return JSON.parse(localStorage.getItem(SYNC_META_KEY) || "null"); } catch { return null; }
}

export type StoredSession = AuthSessionResponse;

// Scope this to explicit user navigation. Session expiry and shared sign-out
// must always be able to remove authenticated views without asking permission.
const navigationGuards = new Set<() => boolean>();
export function registerNavigationGuard(guard: () => boolean): () => void {
  navigationGuards.add(guard);
  return () => { navigationGuards.delete(guard); };
}
export function confirmUserNavigation(): boolean {
  for (const guard of navigationGuards) if (!guard()) return false;
  return true;
}

const HISTORY_POSITION = "__xiaomanHistoryPosition";
const historyObject = (state: unknown): Record<string, unknown> => state && typeof state === "object" && !Array.isArray(state) ? state as Record<string, unknown> : {};
const historyPosition = (state: unknown): number | null => {
  const value = historyObject(state)[HISTORY_POSITION];
  return typeof value === "number" && Number.isSafeInteger(value) ? value : null;
};

/** Keep the current React view mounted until native Back/Forward is accepted. */
export function createHistoryNavigationGuard(
  browserHistory: Pick<History, "state" | "pushState" | "replaceState" | "go">,
  currentUrl: () => string,
  confirm: () => boolean = confirmUserNavigation,
) {
  let current = { position: historyPosition(browserHistory.state) ?? 0, url: currentUrl(), state: historyObject(browserHistory.state) };
  let restoringPosition: number | null = null;
  browserHistory.replaceState({ ...current.state, [HISTORY_POSITION]: current.position }, "", current.url);
  const remember = () => {
    current = { position: historyPosition(browserHistory.state) ?? current.position, url: currentUrl(), state: historyObject(browserHistory.state) };
  };
  return {
    push(url: string) {
      // Preserve the position if another page used replaceState for its query.
      browserHistory.replaceState({ ...historyObject(browserHistory.state), [HISTORY_POSITION]: current.position }, "", currentUrl());
      browserHistory.pushState({ [HISTORY_POSITION]: current.position + 1 }, "", url);
    },
    acceptPop(native: boolean): boolean {
      const destination = historyPosition(browserHistory.state);
      if (restoringPosition !== null) {
        // A restoration popstate is bookkeeping, not a second leave attempt.
        if (destination !== restoringPosition) return false;
        restoringPosition = null; remember(); return true;
      }
      if (!native || confirm()) { remember(); return true; }
      if (destination !== null && destination !== current.position) {
        restoringPosition = current.position;
        browserHistory.go(current.position - destination);
      } else {
        // Legacy unmarked entries cannot reveal their traversal delta. Restore
        // the editor URL with a new entry rather than discard the mounted draft.
        browserHistory.pushState({ ...current.state, [HISTORY_POSITION]: current.position }, "", current.url);
      }
      return false;
    },
  };
}

export function loadSession(): StoredSession | null {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null") as StoredSession | null; } catch { return null; }
}

export function saveSession(session: StoredSession | null, kind: WebSyncMeta["kind"] | "refresh" = session ? "login" : "expired"): void {
  if (kind !== "refresh") localStorage.setItem(SYNC_META_KEY, JSON.stringify({ ...loadSyncMeta(), id: crypto.randomUUID(), kind, at: Date.now() }));
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else localStorage.removeItem(SESSION_KEY);
  window.dispatchEvent(new CustomEvent("agent-session-changed"));
}

const refreshes = new Map<string, Promise<void>>();
export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) { super(message); this.name = "ApiError"; }
}
const sessionChanged = () => new Error("账户已切换，请在当前账户重新操作。");

async function refreshSession(session: StoredSession): Promise<void> {
  const token = session.tokens.refreshToken;
  const existing = refreshes.get(token);
  if (existing) return existing;
  const renew = async () => {
    const current = loadSession();
    if (current?.user.id !== session.user.id) throw sessionChanged();
    if (current.tokens.refreshToken !== token) return;
    const refreshed = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: token }),
    });
    const next = refreshed.ok ? await refreshed.json() as StoredSession : null;
    // A delayed response must never overwrite a logout or another login.
    if (loadSession()?.tokens.refreshToken !== token) throw sessionChanged();
    if (next) {
      if (next.user.id !== session.user.id) throw sessionChanged();
      saveSession(next, "refresh");
    } else {
      if (refreshed.status === 401 || refreshed.status === 403) saveSession(null);
      throw new Error(refreshed.status === 401 || refreshed.status === 403 ? "登录已失效，请重新登录。" : "登录续期暂时没有完成，请稍后重试。");
    }
  };
  // Tabs share rotating credentials. Serializing renewal across the origin
  // prevents a second tab from consuming the same one-use refresh token.
  const work = typeof navigator !== "undefined" && navigator.locks
    ? navigator.locks.request("xiaoman-session-renewal", renew)
    : renew();
  refreshes.set(token, work);
  try { await work; } finally { if (refreshes.get(token) === work) refreshes.delete(token); }
}

export async function request<T>(path: string, init?: RequestInit, retry = true): Promise<T> {
  const session = loadSession();
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (session?.tokens.accessToken) headers.set("Authorization", `Bearer ${session.tokens.accessToken}`);
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (loadSession()?.user.id !== session?.user.id) throw sessionChanged();
  if (response.status === 401 && retry && session?.tokens.refreshToken) {
    // Concurrent requests can share the already-rotated token or one renewal.
    if (loadSession()?.tokens.accessToken === session.tokens.accessToken) await refreshSession(session);
    if (loadSession()?.user.id !== session.user.id) throw sessionChanged();
    return request<T>(path, init, false);
  }
  const body = await response.text();
  if (loadSession()?.user.id !== session?.user.id) throw sessionChanged();
  if (!response.ok) {
    let detail: { message?: string; code?: string } = {};
    try { detail = JSON.parse(body); } catch { /* Preserve the HTTP status even if a proxy returns HTML. */ }
    throw new ApiError(detail.message || `请求失败（${response.status}），请稍后重试。`, response.status, detail.code);
  }
  return (body ? JSON.parse(body) : {}) as T;
}

export const api = {
  sessionTicket: (challenge: string) => request<{ ticket: string; familyId: string }>("/api/v1/auth/session/ticket", { method: "POST", body: JSON.stringify({ challenge, audience: "extension", webOrigin: location.origin }) }),
  exchangeSession: (ticket: string, verifier: string) => request<AuthSessionResponse>("/api/v1/auth/session/exchange", { method: "POST", body: JSON.stringify({ ticket, verifier, audience: "web", webOrigin: location.origin }) }),
  sendSms: (phone: string) => request<{ ok: boolean; testCode?: string }>("/api/v1/auth/sms/send", { method: "POST", body: JSON.stringify({ phone, purpose: "login" }) }),
  loginSms: (phone: string, code: string) => request<AuthSessionResponse>("/api/v1/auth/sms/login", { method: "POST", body: JSON.stringify({ phone, code, purpose: "login" }) }),
  loginPassword: (phone: string, password: string) => request<AuthSessionResponse>("/api/v1/auth/password/login", { method: "POST", body: JSON.stringify({ phone, password }) }),
  setPassword: (password: string) => request<{ ok: true }>("/api/v1/auth/password", { method: "POST", body: JSON.stringify({ password }) }),
  logout: () => request<{ ok: true }>("/api/v1/auth/logout", { method: "POST" }),
  wechatStart: () => request<{ authorizationUrl: string }>("/api/v1/auth/wechat/start"),
  wechatBindStart: () => request<{ authorizationUrl: string }>("/api/v1/auth/wechat/bind/start"),
  wechatExchange: (code: string) => request<AuthSessionResponse>("/api/v1/auth/wechat/exchange", { method: "POST", body: JSON.stringify({ code }) }),
  summary: () => request<AccountSummary>("/api/v1/account/summary"),
  plans: () => request<{ plans: Plan[] }>("/api/v1/catalog/plans"),
  products: () => request<{ products: Product[] }>("/api/v1/catalog/products"),
  points: () => request<{ entries: PointLedgerEntry[] }>("/api/v1/account/points"),
  pointRules: () => request<PointRulesResponse>("/api/v1/catalog/point-rules"),
  paymentOptions: () => request<{ providers: PaymentProviderName[] }>("/api/v1/catalog/payment-options"),
  orders: () => request<{ orders: Order[] }>("/api/v1/orders"),
  tasks: () => request<{ tasks: CoachTaskRecord[] }>("/api/v1/tasks?lifecycle=all&limit=50"),
  growthProfile: () => request<BusinessProfile>("/api/v1/growth/profile"),
  saveGrowthProfile: (data: UpsertBusinessProfileRequest) => request<BusinessProfile>("/api/v1/growth/profile", { method: "PUT", body: JSON.stringify(data) }),
  growthRoute: () => request<{ route: GrowthRoute | null }>("/api/v1/growth/route"),
  createGrowthRoute: () => request<{ route: GrowthRoute }>("/api/v1/growth/route", { method: "POST", body: "{}" }),
  confirmGrowthAssessment: (profileUpdatedAt: string) => request<{ route: GrowthRoute }>("/api/v1/growth/route/assessment", { method: "POST", body: JSON.stringify({ profileUpdatedAt }) }),
  confirmGrowthRegistration: (taskId: string) => request<{ route: GrowthRoute }>(`/api/v1/growth/tasks/${encodeURIComponent(taskId)}/confirm-registration`, { method: "POST", body: JSON.stringify({ confirmed: true }) }),
  confirmGrowthOutcome: (growthTaskId: string, taskId: string) => request<{ route: GrowthRoute }>(`/api/v1/growth/tasks/${encodeURIComponent(growthTaskId)}/confirm-outcome`, { method: "POST", body: JSON.stringify({ taskId, confirmed: true }) }),
  selectGrowthPlatform: (platform: GrowthPlatform, profileUpdatedAt: string) => request<{ route: GrowthRoute | null }>("/api/v1/growth/route/platform", { method: "POST", body: JSON.stringify({ platform, confirmed: true, profileUpdatedAt }) }),
  syncGrowthEvidence: () => request<{ route: GrowthRoute | null }>("/api/v1/growth/route/sync", { method: "POST", body: "{}" }),
  linkGrowthEvidence: (growthTaskId: string, taskId: string) => request<{ route: GrowthRoute }>(`/api/v1/growth/tasks/${encodeURIComponent(growthTaskId)}/evidence`, { method: "POST", body: JSON.stringify({ taskId }) }),
  getOrder: (orderNo: string) => request<Order>(`/api/v1/orders/${encodeURIComponent(orderNo)}`),
  createOrder: (productCode: string) => request<Order>("/api/v1/orders", { method: "POST", body: JSON.stringify({ productCode }) }),
  createPayment: (orderNo: string, provider: PaymentProviderName) => request<CreatePaymentResponse>(`/api/v1/orders/${encodeURIComponent(orderNo)}/payments`, { method: "POST", body: JSON.stringify({ provider }) }),
  mockSucceed: (orderNo: string) => request<Order>(`/api/v1/payments/mock/${encodeURIComponent(orderNo)}/succeed`, { method: "POST" }),
  humanSupport: (orderNo: string) => request<HumanSupportTicket>(`/api/v1/human-support/${encodeURIComponent(orderNo)}`),
  humanSupportDraft: (draftId: string) => request<HumanSupportDraft>(`/api/v1/human-support/drafts/${encodeURIComponent(draftId)}`),
  createHumanSupportDraft: (context: HumanSupportContext) => request<HumanSupportDraft>("/api/v1/human-support/drafts", { method: "POST", body: JSON.stringify(context) }),
  updateHumanSupportDraft: (draftId: string, context: HumanSupportContext) => request<HumanSupportDraft>(`/api/v1/human-support/drafts/${encodeURIComponent(draftId)}`, { method: "PUT", body: JSON.stringify(context) }),
  attachHumanSupportDraft: (orderNo: string, draftId: string) => request<HumanSupportTicket>(`/api/v1/human-support/${encodeURIComponent(orderNo)}/attach-draft`, { method: "POST", body: JSON.stringify({ draftId }) }),
  humanSupportTickets: () => request<{ tickets: HumanSupportTicket[] }>("/api/v1/human-support"),
  adminUsers: () => request<{ users: Array<{ id: string; phone: string | null; role: string; status: string; created_at: string }> }>("/api/v1/admin/users"),
  adminOrders: () => request<{ orders: Array<{ order_no: string; product_name: string; phone: string | null; amount_cents: number; status: string; created_at: string }> }>("/api/v1/admin/orders"),
  updatePlan: (code: string, data: { dailyGrantPoints: number }) => request(`/api/v1/admin/plans/${code}`, { method: "PUT", body: JSON.stringify(data) }),
  upsertProduct: (data: { code: string; name: string; description: string; kind: string; planCode: string | null; priceCents: number; grantPoints: number; active: boolean }) => request("/api/v1/admin/products", { method: "POST", body: JSON.stringify(data) }),
  updateSupportQr: (imageUrl: string) => request("/api/v1/admin/settings/human-support-qr", { method: "PUT", body: JSON.stringify({ imageUrl }) }),
  commercialReadiness: () => request<CommercialReadiness>("/api/v1/admin/commercial/readiness"),
  recordCommercialVerification: (key: ProductionVerificationKey, data: { evidenceRef: string; expiresAt: string }) => request<{ ok: true }>(`/api/v1/admin/commercial/verifications/${encodeURIComponent(key)}`, { method: "PUT", body: JSON.stringify(data) }),
  reconcile: (from: string, to: string) => request<ReconciliationSummary>("/api/v1/admin/commercial/reconciliations", { method: "POST", body: JSON.stringify({ from, to }) }),
  adminHumanSupport: () => request<{ tickets: HumanSupportTicket[] }>("/api/v1/admin/human-support"),
  adminHumanSupportDetail: (serviceNo: string) => request<HumanSupportTicket>(`/api/v1/admin/human-support/${encodeURIComponent(serviceNo)}`),
  updateHumanSupport: (serviceNo: string, data: { status: "in_progress" | "completed" | "refunding" | "refunded"; operatorNote?: string; resolution?: string; reusableKnowledge?: boolean }) => request<HumanSupportTicket>(`/api/v1/admin/human-support/${encodeURIComponent(serviceNo)}`, { method: "PATCH", body: JSON.stringify(data) }),
  adminKnowledge: () => request<{ items: KnowledgeItem[] }>("/api/v1/admin/knowledge"),
  adminQualityReviews: () => request<{ reviews: KnowledgeReviewItem[] }>("/api/v1/admin/quality/reviews?status=open"),
  createKnowledge: (data: { title: string; answer: string; platform: KnowledgeItem["platform"]; site: string; scope: string; sources: Array<{ title: string; url: string; type: "official" }>; nextReviewAt: string; reviewItemId?: string }) => request<KnowledgeItem>("/api/v1/admin/knowledge", { method: "POST", body: JSON.stringify(data) }),
  reviewKnowledge: (itemId: string, data: { decision: "approve" | "request_changes" | "retire"; note: string; nextReviewAt?: string }) => request<KnowledgeItem>(`/api/v1/admin/knowledge/${encodeURIComponent(itemId)}/review`, { method: "POST", body: JSON.stringify(data) }),
  resolveQualityReview: (reviewId: string, data: { status: "in_review" | "resolved" | "rejected"; cause?: "knowledge" | "intent" | "page" | "model" | "product" | "other"; resolutionNote: string; knowledgeItemId?: string }) => request<KnowledgeReviewItem>(`/api/v1/admin/quality/reviews/${encodeURIComponent(reviewId)}`, { method: "POST", body: JSON.stringify(data) }),
};

export { API_BASE };
